import { Search, Crosshair, Rocket, TrendingUp, ArrowRight } from 'lucide-react';

const steps = [
  {
    number: '01',
    icon: Search,
    title: 'Deep Audit',
    description: 'We audit your current marketing stack, ad accounts, funnels, and analytics to identify every gap and missed opportunity.',
    details: ['Account audit', 'Competitor analysis', 'Funnel review', 'Data infrastructure'],
    color: 'from-blue-500 to-cyan-500',
  },
  {
    number: '02',
    icon: Crosshair,
    title: 'Precision Strategy',
    description: 'We build a custom growth roadmap with channel mix, audience targeting, creative direction, and budget allocation tailored to your goals.',
    details: ['Channel strategy', 'Audience research', 'Creative brief', 'Budget modeling'],
    color: 'from-violet-500 to-purple-500',
  },
  {
    number: '03',
    icon: Rocket,
    title: 'Rapid Launch',
    description: 'Campaigns go live within 48 hours. We deploy ads, funnels, and tracking in parallel — no wasted time, no bottlenecks.',
    details: ['Campaign build', 'Funnel deployment', 'Pixel & tracking', 'QA testing'],
    color: 'from-emerald-500 to-teal-500',
  },
  {
    number: '04',
    icon: TrendingUp,
    title: 'Scale & Optimize',
    description: 'Continuous optimization through data analysis, creative testing, and systematic scaling of winning campaigns for maximum ROI.',
    details: ['A/B testing', 'Budget scaling', 'Creative refresh', 'Performance reports'],
    color: 'from-amber-500 to-orange-500',
  },
];

export default function HowItWorks() {
  return (
    <section id="process" data-testid="process-section" className="section-padding bg-vertex-gradient relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-vertex-radial" />
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-vertex-500/30 to-transparent" />
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-20">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-vertex-500/10 border border-vertex-500/20 mb-6">
            <span className="text-vertex-300 text-sm font-semibold">Our Process</span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-white mb-5">
            From Audit to Scale in
            <span className="gradient-text"> 4 Steps</span>
          </h2>
          <p className="text-gray-400 text-lg leading-relaxed">
            A proven, repeatable framework that eliminates guesswork and delivers predictable growth.
          </p>
        </div>

        {/* Steps */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {steps.map((step, i) => (
            <div key={step.number} className="relative group">
              {/* Connector line (desktop) */}
              {i < steps.length - 1 && (
                <div className="hidden lg:block absolute top-16 left-full w-full h-px z-10">
                  <div className="w-full h-px bg-gradient-to-r from-vertex-500/40 to-vertex-500/10" />
                  <ArrowRight className="absolute -right-2 -top-2 w-4 h-4 text-vertex-500/40" />
                </div>
              )}

              <div className="glass rounded-2xl p-6 h-full card-hover group-hover:border-vertex-500/30">
                {/* Step Number */}
                <div className={`text-5xl font-display font-black bg-gradient-to-br ${step.color} bg-clip-text text-transparent opacity-30 mb-4`}>
                  {step.number}
                </div>

                {/* Icon */}
                <div className={`inline-flex p-3 rounded-xl bg-gradient-to-br ${step.color} mb-4 shadow-lg`}>
                  <step.icon className="w-5 h-5 text-white" />
                </div>

                {/* Content */}
                <h3 className="text-xl font-display font-bold text-white mb-3">{step.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed mb-4">{step.description}</p>

                {/* Details */}
                <div className="space-y-2">
                  {step.details.map((detail) => (
                    <div key={detail} className="flex items-center gap-2 text-gray-500 text-xs">
                      <div className="w-1.5 h-1.5 rounded-full bg-vertex-500/50" />
                      {detail}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
